<template>
  <div class="orders d-flex direction-column">
    <app-header-container>
      <div class="d-flex align-center">
        {{ $t('views.Orders.title') }}
      </div>

      <template v-slot:controls>
        <div class="d-flex align-center justify-end">
          <autocomplete
            not-empty
            value-field="id"
            item-text="fullName"
            v-model="activeContractId"
            :items="contractsWithNull"
            :placeholder="$t('views.Orders.contractSelect.label')"
            :label="$t('views.Orders.contractSelect.label')"
            v-if="contracts.length && screen.breakPoint.tabPort"
            class="mr-2"
          ></autocomplete>
          <add-order v-if="ShowOrderButton" @add="addOrder" :loading="isOrderAdding" v-model="showAddOrder"></add-order>
        </div>
      </template>
<!--      <template v-slot:interactiveControls><v-button class="ml-auto" color="success" :size="screen.breakPoint.tabPort ? null : 'xs-small'">{{ $t('views.Dashboard.Orders.addOrder') }}</v-button></template>-->
    </app-header-container>

    <app-actions-container v-if="!screen.breakPoint.tabPort">
      <date-picker :value="datePickerAttrs" @change="setDatePickerAttrs"></date-picker>
    </app-actions-container>

    <app-controls-container no-padding>
      <div class="mb-2" v-if="contracts.length && !screen.breakPoint.tabPort">
        <autocomplete
          not-empty
          value-field="id"
          item-text="fullName"
          v-model="activeContractId"
          :items="contractsWithNull"
          :placeholder="$t('views.Orders.contractSelect.label')"
          :label="$t('views.Orders.contractSelect.label')"
        ></autocomplete>
      </div>
      <div class="text--xs-small text--uppercase text--bold text--kerning-large mb-1">{{$t('views.Orders.orderSource')}}</div>
      <btn-filter :active-filter.sync="sourceFilter" :filters="sources" :vertical="screen.breakPoint.tabLand" class="mb-2"></btn-filter>
      <div class="text--xs-small text--uppercase text--bold text--kerning-large mb-1">{{ $t('views.Orders.status.title') }}</div>
      <btn-filter :active-filter.sync="activeFilter" :filters="filters" :vertical="screen.breakPoint.tabLand"></btn-filter>

      <action-link class="set-indent-mobile-only mt-2" icon-right="icon-srw" v-if="screen.breakPoint.tabLand" @click="downloadCSV()">
        {{$t('views.Orders.downloadCSV')}}
      </action-link>
    </app-controls-container>

    <div class="color-preview d-flex align-center py-2">
      <div>
        <div class="text--xs-small text--uppercase text--bold text--kerning-large mb-1">{{$t('views.Dashboard.Orders.colors')}}:</div>
        <div class="color-preview__inner d-flex align-center">
          <div>
            <popover left>
              <template v-slot:activator><span class="color-preview__tag bg-error"></span></template>
              {{$t('views.Dashboard.Orders.legend1')}}
            </popover>
          </div>
          <div>
            <popover left>
              <template v-slot:activator><span class="color-preview__tag bg-blue-semantic"></span></template>
              {{$t('views.Dashboard.Orders.legend2')}}
            </popover>
          </div>
          <div>
            <popover left>
              <template v-slot:activator><span class="color-preview__tag bg-purple-black"></span></template>
              {{$t('views.Dashboard.Orders.legend3')}}
            </popover>
          </div>
          <div>
            <popover>
              <template v-slot:activator><span class="color-preview__tag bg-grey-light"></span></template>
              {{$t('views.Dashboard.Orders.legend4')}}
            </popover>
          </div>
        </div>
      </div>

      <div class="ml-auto d-flex align-center">
        <div>
          <div class="text--xs-small text--uppercase text--bold text--kerning-large mb-1">{{$t('views.Dashboard.Orders.period')}}:</div>
          <date-picker :value="datePickerAttrs" @change="setDatePickerAttrs"></date-picker>
        </div>
      </div>
    </div>

    <orders-list
      date-field="orderedAt"
      :orders="orders ? orders.content : []"
      :loading="loading"
      :title-max-length="titleMaxLength"

      @accept="acceptOrder"
      @decline="e => openDeclinePartnerComment(e, false)"
      @revert="e => openDeclinePartnerComment(e, true)"
    ></orders-list>

    <list-footer
      :loading="fetchingMore || $apollo.queries.orders.loading"
      :total="!orders ? 0 : orders.totalElements"
    ></list-footer>

    <v-modal no-padding animation="slide-up" :full="!screen.breakPoint.tabPort">
      <template v-slot:header>{{$t('views.Orders.selectDate')}}</template>
    </v-modal>

    <decline-partner-comment
      v-model="showDeclinePartnerComment"
      :comment="declinePartnerComment"

      @change="(e) => declinePartnerComment = e"
      @confirm="confirmChangeOrderStatus()"
      @cancel="showDeclinePartnerComment = false"
    ></decline-partner-comment>
  </div>
</template>

<script>
import AppHeaderContainer from '@/ui/layout/AppHeaderContainer'
import AppActionsContainer from '@/ui/layout/AppActionsContainer'
import AppControlsContainer from '@/ui/layout/AppControlsContainer'

import ListFooter from '@/ui/slots/List/ListFooter'
import ActionLink from '@/ui/controls/ActionLink'
import Autocomplete from '@/ui/controls/Autocomplete'
import Popover from '@/ui/modals/Popover'

import vModal from '@/ui/modals/vModal'

import BtnFilter from '@/components/BtnFilter'
import OrdersList from '@/components/OrdersList'
import AddOrder from '@/components/AddOrder'
import DeclinePartnerComment from '@/components/DeclinePartnerComment'

import toCurrency from '@/mixins/toCurrency'
import screen from '@/mixins/screen'
import currContractId from '@/mixins/currContractId'
import contracts from '@/mixins/contracts'

import fetchMore from '@/mixins/fetchMore'
import { notifyGraphqlError } from '@/mixins/handleGraphqlError'
import { ShowOrderButton } from '@/mixins/displayRules'
import shouldReFetchOrdersGetters from '@/mixins/shouldReFetchOrders'

import { OrdersGQL, SettlementsGQL } from '@/graphql/queries'
import { AddOrderGQL, ChangeOrderStatusGQL } from '@/graphql/mutations'

import { now, threeMonthAgo, threeYearsAgo } from '@/utils'

import { AUTH_TOKEN } from '@/vue-apollo'
const queryVars = {
  descending: true,
  orderedAtFrom: threeMonthAgo().toISOString(),
  orderedAtTo: now.toISOString(),
  sortBy: 'ORDERED_AT'
}

const paginationVars = {
  page: 0,
  size: 15
}

const activeFilter = {
  value: null
  // name: 'Все'
}

const sourceFilter = {
  value: null
}

export default {
  mixins: [toCurrency, screen, currContractId, fetchMore, contracts, ShowOrderButton, notifyGraphqlError, shouldReFetchOrdersGetters],

  components: {
    AppHeaderContainer,
    AppActionsContainer,
    AppControlsContainer,

    ListFooter,
    Autocomplete,

    BtnFilter,
    ActionLink,
    Popover,

    OrdersList,
    AddOrder,
    DeclinePartnerComment,

    DatePicker: () => import('@/ui/controls/DatePicker'),

    vModal
  },

  apollo: {
    orders: {
      query: OrdersGQL,
      variables () {
        return {
          params: {
            ...this.queryVars,
            ...this.paginationVars,
            status: this.activeFilter.value,
            typeAddEnum: this.sourceFilter.value,
            partnerContractId: this.activeContractId
          }
        }
      },
      update: data => data.merchant.orders,
      skip () {
        return !this.currContractId
      }
    },

    settlements: {
      query: SettlementsGQL,
      variables () {
        return {
          params: {
            partnerContractId: this.currContractId
          }
        }
      },
      update: data => data.merchant.settlements,
      skip () {
        return !this.currContractId
      }
    }
  },

  data () {
    return {
      queryVars,
      paginationVars,
      activeFilter,
      sourceFilter,

      isOrderAdding: false,
      showAddOrder: false,
      isChangingStatus: false,

      showDeclinePartnerComment: false,
      declinePartnerComment: null,
      isRevert: false,

      activeContractId: null
    }
  },

  computed: {
    titleMaxLength () {
      if (this.screen.breakPoint.tabPort) {
        return 25
      }

      return 15
    },

    filters () {
      return [
        {
          value: null,
          name: this.$i18n.t('views.Orders.status.all')
        },
        {
          value: 'PENDING',
          name: this.$i18n.t('views.Orders.status.pending')
        },
        {
          value: 'CHECK',
          name: this.$i18n.t('views.Orders.status.check')
        },
        {
          value: 'APPROVED',
          name: this.$i18n.t('views.Orders.status.approved')
        },
        {
          value: 'DECLINED',
          name: this.$i18n.t('views.Orders.status.declined')
        }
      ]
    },

    sources () {
      return [
        {
          value: null,
          name: this.$i18n.t('views.Orders.status.all')
        },
        {
          value: 'MANUAL',
          name: this.$i18n.t('views.Orders.orderAddedManually')
        },
        {
          value: 'PARTNER_FIND_MANUAL',
          name: this.$i18n.t('views.Orders.partnerSelectedManually')
        }
      ]
    },

    loading () {
      return this.$apollo.queries.orders && this.$apollo.queries.orders.loading && !this.fetchingMore
    },

    loadingMore () {
      return this.orders && this.$apollo.queries.orders && this.$apollo.queries.orders.loading && this.fetchingMore
    },

    datePickerAttrs () {
      return { start: this.queryVars.orderedAtFrom, end: this.queryVars.orderedAtTo }
    }
  },

  methods: {
    setDatePickerAttrs (e) {
      this.queryVars = {
        ...this.queryVars,
        orderedAtFrom: e.start,
        orderedAtTo: e.end
      }
    },

    async downloadCSV () {
      const token = await this.$storage.get({ key: AUTH_TOKEN }).then(res => res.value)

      if (token) {
        window.location.assign(`${this.$getEnv.backendPath}/rest/v1/private/${new Date(this.queryVars.orderedAtFrom).getTime()}/${new Date(this.queryVars.orderedAtTo).getTime()}/client-order-list.csv?s=${token}`)
      }
    },

    showMore () {
      if (!this.orders) {
        return
      }

      const params = {
        ...this.queryVars,
        ...this.paginationVars,
        status: this.activeFilter.value,
        typeAddEnum: this.sourceFilter.value,
        partnerContractId: this.activeContractId
      }

      this.fetchMore({
        params,
        rootField: 'merchant',
        apolloQueryField: 'orders',
        currPage: this.orders.currentPage
      })
    },

    async addOrder (order) {
      try {
        this.isOrderAdding = true
        await this.$apollo.mutate({
          mutation: AddOrderGQL,
          variables: {
            params: order
          }
        })

        this.isOrderAdding = false
        this.showAddOrder = false
        this.$notify({
          type: 'success',
          message: this.$i18n.t('views.Orders.orderAdded'),
          delay: 150,
          timeout: 3000
        })

        await this.$apollo.queries.orders.refetch()
        await this.$apollo.queries.settlements.refetch()
      } catch (e) {
        this.isOrderAdding = false
        this.notifyGraphqlError(e)
      }
    },

    acceptOrder ({ id }) {
      this.$apollo.mutate({
        mutation: ChangeOrderStatusGQL,
        variables: {
          params: {
            declinePartnerComment: null,
            id,
            partnerApproved: true
          }
        }
      })
    },

    openDeclinePartnerComment ({ id }, isRevert) {
      this.declineOrderId = id
      this.isRevert = isRevert
      this.showDeclinePartnerComment = true
    },

    confirmChangeOrderStatus () {
      if (this.isRevert) {
        this.revertOrder()
      } else {
        this.declineOrder()
      }
    },

    async declineOrder () {
      try {
        await this.$apollo.mutate({
          mutation: ChangeOrderStatusGQL,
          variables: {
            params: {
              declinePartnerComment: this.declinePartnerComment,
              id: this.declineOrderId,
              partnerApproved: false
            }
          }
        })
      } catch (e) {
        this.notifyGraphqlError(e)
      }

      this.showDeclinePartnerComment = false
    },

    async revertOrder () {
      try {
        await this.$apollo.mutate({
          mutation: ChangeOrderStatusGQL,
          variables: {
            params: {
              declinePartnerComment: this.declinePartnerComment,
              id: this.declineOrderId,
              partnerApproved: null
            }
          }
        })
      } catch (e) {
        this.notifyGraphqlError(e)
      }

      this.showDeclinePartnerComment = false
    }
  },

  watch: {
    showDeclinePartnerComment (val) {
      if (val === false) {
        this.declinePartnerComment = null
      }
    }
  },

  mounted () {
    document.addEventListener('reachedBottom', this.showMore)

    if (this.$route.query.status === 'check') {
      this.activeFilter = {
        value: 'CHECK'
      }
    }

    if (this.$route.query.status === 'pending') {
      this.activeFilter = {
        value: 'PENDING'
      }
    }

    if (this.$route.query.contract) {
      this.activeContractId = +this.$route.query.contract
      this.queryVars.orderedAtFrom = threeYearsAgo(now).toISOString()
      this.queryVars.orderedAtTo = now.toISOString()
    }

    if (this.shouldReFetchOrdersGetters.shouldReFetch && this.shouldReFetchOrdersGetters.fetched) {
      setTimeout(() => {
        this.$apollo.queries.orders.refetch()
        this.setShouldOrdersReFetch(false)
      }, 200)
    }

    this.setOrdersFetched(true)
  },

  beforeDestroy () {
    document.removeEventListener('reachedBottom', this.showMore)
  }
}
</script>

<style lang="scss">
  .orders {
    &__group {
      &:not(:last-child) {
        margin-bottom: 2rem;
      }
    }

    &__footer {
      border-top: 1px $color-grey-light solid;
      text-align: center;
    }
  }

  .color-preview {
    &__inner > *:not(:last-child) {
      margin-right: 1rem;
    }

    &__tag {
      position: relative;
      top: -1px;
      display: inline-block;
      width: 20px;
      height: 20px;
      border-radius: 100px;
      vertical-align: middle;
    }
  }
</style>
